import * as React from 'react'
import Certificate, { NominalListItem } from '../../shared/types'
import { FormEventHandler, useState } from 'react'
import { sendPaymentRequest } from '../../shared/lib'
import { routeToPage, setPaymentInfo } from '../../shared/state'
import classNames from 'classnames'
import Input from '../../shared/ui/input'
import { z } from 'zod'
import s from './index.module.scss'
import Button from '../../shared/ui/button'
import { Popover } from 'antd'

const BuyForm: React.FC<{ certificate: Certificate; giftGuid?: string }> = ({ certificate, giftGuid }) => {
    const [selectedPrice, setSelectedPrice] = useState<NominalListItem>(certificate.nominal_list[0])
    const [email, setEmail] = useState('')

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    const emailSchema = z.string().regex(emailRegex, certificate.warning_2)

    const [emailError, setEmailError] = useState<string | null>(null)
    // Добавлено 25.10.2025
    const valueOf = (it?: { obs_type_id?: string; nominal?: string | number } | null) =>
        it?.obs_type_id ?? String(it?.nominal ?? '')

    const isSelected = (it?: { obs_type_id?: string; nominal?: string | number } | null) =>
        valueOf(it) === valueOf(selectedPrice)
    // Конец добавления 25.10.2025

    //Добавлено 26.10.2025
    // -------- ДОБАВИТЕ СРАЗУ ПОСЛЕ СТЕЙТОВ/ПЕРЕМЕННЫХ КОМПОНЕНТА --------
    const [customNominal, setCustomNominal] = useState<string>('') // строкой удобно держать ввод
    const isCustomSelected = (selectedPrice?.obs_type_id ?? String(selectedPrice?.nominal ?? '')) === 'CUSTOM_NOMINAL'
    const customAmountOk = !isCustomSelected || Number(selectedPrice?.price) > 0
    //Конец добавления 26.10.2025
    // inline error for the custom amount - 02.11.2025
    const [amountError, setAmountError] = useState<string | null>(null)

    const onSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault()

        try {
            emailSchema.parse(email)
            setEmailError(null)
        } catch (e) {
            if (e instanceof z.ZodError) {
                const errs = e.errors.map((err) => err.message)
                setEmailError(errs[0])
            }
            return
        }

        // 2) выбран ли "Ваш номинал"
        const isCustomSelected =
            (selectedPrice?.obs_type_id ?? String(selectedPrice?.nominal ?? '')) === 'CUSTOM_NOMINAL'

        // 3) obs_type_id верхней (самой первой) кнопки
        const firstObsTypeId = certificate?.nominal_list?.[0]?.obs_type_id ?? ''

        // 4) подготовим данные для отправки
        let nominalToSend: string
        let obsTypeIdToSend: string

        if (isCustomSelected) {
            const customAmountStr = (customNominal ?? '').trim()

            const digitsOnly = /^[0-9]+$/
            if (!digitsOnly.test(customAmountStr)) {
                setAmountError('Сумма должна быть положительным целым числом (только цифры).') //should be replaced later with certificate?.custom_error_positive_integer?.trim() which should come from backend for making it belingual. English text: Amount must be a positive integer (digits only)
                return
            }

            const n = Number(customAmountStr)
            if (!isFinite(n) || n <= 0) {
                setAmountError('Сумма должна быть больше нуля.') //Amount must be greater than zero - should be replaced later with certificate?.custom_error_abovezero?.trim()
                return
            }

            if (!Number.isInteger(n)) {
                setAmountError('Сумма должна быть целым числом (без десятичных дробей).') //Amount must be a whole number (no decimals) - should be replaced later with certificate?.custom_error_nodecimal?.trim()
                return
            }

            const MAX_AMOUNT = 1_000_000_000
            if (n > MAX_AMOUNT) {
                setAmountError('Сумма не должна превышать 1,000,000,000.') //Amount must not exceed 1,000,000,000.
                return
            }

            if (!firstObsTypeId) {
                setAmountError('Base nominal type (obs_type_id) not found.')
                return
            }
            // clear error before proceeding - 02.11.2025
            setAmountError(null)

            nominalToSend = customAmountStr // отправляем то, что ввёл пользователь
            obsTypeIdToSend = firstObsTypeId // как у первой кнопки
        } else {
            setAmountError(null) // not custom — make sure old error goes away - 02.11.2025
            nominalToSend = String(selectedPrice?.nominal ?? '')
            obsTypeIdToSend = String(selectedPrice?.obs_type_id ?? '')
        }

        // 5) вызов API с нужными полями + безопасный разбор ответа
        let result: any
        try {
            result = await sendPaymentRequest({
                nominal: nominalToSend,
                obs_type_id: obsTypeIdToSend,
                email: email,
                quantity: '1',
                guid: giftGuid ?? '',
            })
        } catch (apiErr) {
            console.error(apiErr)
            alert('Ошибка сети или сервера при создании платежа.')
            return
        }

        const token = result?.payment?.confirmation?.confirmation_token
        if (!token) {
            const serverMsg =
                result?.res_msg || result?.message || 'Не удалось создать платёж. Проверьте сумму и попробуйте ещё раз.'
            alert(serverMsg)
            return
        }

        // 6) сохраняем платёж и переходим к оплате
        setPaymentInfo({
            email: email,
            quantity: '1',
            // price оставляем строкой: для кастома — введённая сумма, иначе — как было
            price: isCustomSelected ? nominalToSend : String(selectedPrice?.price ?? selectedPrice?.nominal ?? ''),
            confirmation_token: token,
        })

        routeToPage('pay')
    }

    return (
        <div>
            {certificate.nominal_exist && (
                <div className={s.nominalWrapper}>
                    <b>{certificate.nominal_title}:</b>
                    {certificate.nominal_list.map((item) => (
                        <button
                            key={item.nominal}
                            className={classNames(s.item, { [s.selected]: item.nominal == selectedPrice.nominal })}
                            onClick={() => {
                                setAmountError(null) // ← add this - 02.11.2025
                                setSelectedPrice(item)
                            }}
                        >
                            {item.nominal} {certificate.nominal_currency_symbol}
                        </button>
                    ))}

                    {/* Кнопка "Ваш номинал" — показывать только при flexible & customer */}
                    {Number(certificate?.nominal_flexible) === 1 && Number(certificate?.nominal_customer) === 1 && (
                        <button
                            key="CUSTOM_NOMINAL"
                            //className={s.item} // тот же класс, что и у остальных
                            // временно без onClick-логики выбора суммы; добавим позже при необходимости
                            className={classNames(s.item, {
                                [s.selected]: valueOf(selectedPrice) === 'CUSTOM_NOMINAL',
                            })}
                            type="button"
                            onClick={() => {
                                setAmountError(null) //02.11.2025
                                setSelectedPrice({
                                    obs_type_id: 'CUSTOM_NOMINAL', // маркер значения
                                    nominal: 'CUSTOM_NOMINAL', // дублируем для совместимости с существующей логикой
                                    price: '0', // заполняйте как нужно вашей логике
                                    currency: 'CURRENCY',
                                    price_currency: 'PRICE_CURRENCY',
                                })
                                setCustomNominal('') // 26.10.2025 очистим ввод при новом выборе
                            }}
                        >
                            {certificate?.custom_amount_title?.trim()}
                        </button>
                    )}
                    {Number(certificate?.nominal_flexible) === 1 &&
                        Number(certificate?.nominal_customer) === 1 &&
                        (selectedPrice?.obs_type_id ?? String(selectedPrice?.nominal ?? '')) === 'CUSTOM_NOMINAL' && (
                            <div className={s.field}>
                                <Input
                                    type="number"
                                    placeholder={String(certificate?.custom_amount ?? '').trim()}
                                    value={customNominal}
                                    error={amountError} //{/* ← как у e-mail */}
                                    onChange={(v) => {
                                        setAmountError(null)
                                        setCustomNominal(v) // drives the input UI
                                        setSelectedPrice((prev) =>
                                            (prev?.obs_type_id ?? String(prev?.nominal ?? '')) === 'CUSTOM_NOMINAL'
                                                ? { ...prev, price: v } // keep as string
                                                : prev,
                                        )
                                    }}
                                />
                            </div>
                        )}
                    {/* 25.10.2025 - Конец моего блок Кнопка "Ваш номинал" — показывать только при flexible & customer */}
                </div>
            )}
            <form onSubmit={onSubmit} className={s.form}>
                <div className={s.field}>
                    <b>{certificate.address_title}:</b>
                    <Input
                        type="text"
                        placeholder={certificate.address_text}
                        value={email}
                        onChange={(v) => setEmail(v)}
                        error={emailError}
                    />
                </div>

                {!certificate.nominal_exist ? (
                    <Popover content={<div>{certificate.no_nominal_text}</div>} trigger="click">
                        <Button type="button">{certificate.text_button_2}</Button>
                    </Popover>
                ) : (
                    <Button>{certificate.text_button_2}</Button>
                )}
            </form>
        </div>
    )
}
export default BuyForm
